/*
 * @Description: 邮箱验证窗口
 * @Author: kadenwang@tencent.com
 * @Date: 2021-11-15 18:04:13
 * @LastEditors: kadenwang@tencent.com
 * @LastEditTime: 2022-07-01 17:05:19
 */

import { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { BaseModel, Button, Input, Toast } from '../../component';
import { useForm } from 'react-hook-form';
import { eventBus, isAppMarket, getMailCgiHost } from '@tencent/syzs-login-sdk-utils';
import { Ipc } from '@tencent/halo-jsbridge-syzs';
import { LoginRequest, postMessageToParent } from '../../lib';
import classNames from 'classnames';
import i18n from '../../i18n';

import './MailVerificationMain.less';

const MAX_COUNTDOWN_SECONDS = 60;

export enum IGetRobotCode {
  'NOT_NEED_ROBOT' = 0,
  'NEED_ROBOT' = -21,
}

export enum ISendBindingCode {
  'SUCCESS' = 0,
  'USER_HAS_EMAIL' = -1,
  'EMAIL_EXISTS' = -2,
  'SEND_EMAIL_FAIL' = -3,
}

export enum ICheckRobotCode {
  'SUCCESS' = 0,
  'ROBOT_NOT_MATCH' = -31,
  'ROBOT_EXITS' = -32,
}

export enum IVerificationCode {
  'SUCCESS' = 0,
  'VCODE_NOT_EQUAL' = -11,
  'VCODE_NOT_EXPIRED' = -12,
}

export interface IError {
  msg: string;
  label?: string;
}

interface Inputs {
  captcha: string;
  vcode: string;
  email: string;
}

const MailVerificationMain = () => {
  const [countdown, setCountdown] = useState(MAX_COUNTDOWN_SECONDS);
  const { register, watch, setError, formState, trigger } = useForm<Inputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
  });
  const { errors } = formState;
  const [captchaImg, setCaptchaImg] = useState('');
  const countDownTimer = useRef<NodeJS.Timeout>();
  const countDownSeconds = useRef<number>(countdown);
  const watchFields = watch();
  const { captcha, email, vcode } = watchFields;
  const [hasFormError, setHasFormError] = useState(true);
  const [checkedKey, setCheckedKey] = useState<string[]>([]);

  const needCheckKeys = useMemo(() => Object.keys(watchFields), [watchFields]);

  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      setHasFormError(true);
    } else {
      if (needCheckKeys.length !== checkedKey.length && vcode.length !== 6) {
        setHasFormError(true);
      } else {
        setHasFormError(false);
      }
    }
  }, [checkedKey.length, formState, needCheckKeys.length, vcode]);

  useEffect(() => {
    const asyncTrigger = async () => {
      if (vcode && vcode.length === 6) {
        const result = await trigger();

        if (result) {
          setHasFormError(false);
        }
      }
    };

    void asyncTrigger();
  }, [trigger, vcode]);

  const handleBlurChange = useCallback((key: string) => {
    if (checkedKey.includes(key)) return;
    const newCheckedKey = checkedKey.concat([key]);

    setCheckedKey(newCheckedKey);
  }, [checkedKey]);

  const getRobotCode = useCallback(async (emailCode?: string): Promise<boolean> => {
    const result = await LoginRequest.request(`${getMailCgiHost()}/v3/get_robot_code`, { email: emailCode }, 'gameloop_store');
    const { code, pic } = result;

    if (code === IGetRobotCode.NEED_ROBOT) {
      setCaptchaImg(pic);

      return true;
    }

    return false;
  }, []);

  const checkRobotCode = useCallback(async (): Promise<boolean> => {
    if (!captcha) return false;

    const result = await LoginRequest.request(`${getMailCgiHost()}/v3/check_robot_code`, {
      robot_code: captcha,
    }, 'gameloop_store');

    const { code, msg } = result;

    if (code === ICheckRobotCode.SUCCESS) return true;

    setError('captcha', {
      type: 'error',
      message: msg,
    });

    window.aegis.infoAll(`check_robot_code Error ${JSON.stringify(result)}`);

    await getRobotCode();

    return false;
  }, [captcha, getRobotCode, setError]);

  const commonVerific = useCallback(async (): Promise<boolean> => {
    if (captchaImg && !captcha) {
      setError('captcha', {
        type: 'error',
        message: i18n.l('CheckRequiredField'),
      });

      return false;
    }

    return hasFormError;
  }, [captcha, captchaImg, hasFormError, setError]);

  const submitMailVerification = useCallback(async () => {
    const commonVerificResult = await commonVerific();
    if (commonVerificResult) return;

    const result = await LoginRequest.request(`${getMailCgiHost()}/v3/check_binding_code`, {
      email,
      verification_code: vcode,
    }, 'gameloop_store');

    const { code, msg } = result;

    if (code === IVerificationCode.SUCCESS) {
      Toast.success(i18n.l('HoundSuccess'));
      LoginRequest.setLoginCookie();

      window.report('mail_verification_bind_success');

      eventBus.emit('USER_BIND_MAIL_SUCCESS', email);
    } else {
      setError('vcode', {
        type: 'error',
        message: msg,
      });

      window.aegis.infoAll(`check_binding_code Error ${JSON.stringify(result)}`);
    }
  }, [commonVerific, email, setError, vcode]);

  const handleClickSendCode = useCallback(async () => {
    const result = await trigger('email');
    if (!result) return;

    if (countdown !== MAX_COUNTDOWN_SECONDS) return;

    if (captchaImg) {
      const checkRobotCodeResult = await checkRobotCode();

      if (!checkRobotCodeResult) {
        setError('captcha', {
          type: 'error',
          message: i18n.l('VcodeIError'),
        });

        return;
      }
    } else {
      // 没有验证码要确认下要不要拉验证码
      const isNeedCaptcha = await getRobotCode(email);
      if (isNeedCaptcha) return;
    }

    countDownTimer.current = setInterval(() => {
      countDownSeconds.current = countDownSeconds.current - 1;
      setCountdown(countDownSeconds.current);

      if (countDownSeconds.current < 0) {
        countDownTimer.current && clearTimeout(countDownTimer.current);
        countDownSeconds.current = MAX_COUNTDOWN_SECONDS;

        setCountdown(MAX_COUNTDOWN_SECONDS);
      }
    }, 1000);

    const bindResult = await LoginRequest.request(`${getMailCgiHost()}/v3/send_binding_code`, {
      email,
    }, 'gameloop_store');

    const { code, msg } = bindResult;

    if (code !== 0) {
      setError('email', {
        type: 'error',
        message: msg,
      });

      window.aegis.report(`send_binding_code Error ${JSON.stringify(bindResult)}`);
    }
  }, [trigger, countdown, captchaImg, email, checkRobotCode, setError, getRobotCode]);

  const sendCodeButton = useMemo(() => {
    const isDisable = countdown !== MAX_COUNTDOWN_SECONDS;
    const text = isDisable ? `${i18n.l('SendCode')} (${countdown}s)` : i18n.l('SendCode');

    return (<div className={classNames('send-code-button', { isDisable })} onClick={handleClickSendCode}>{text}</div>);
  }, [countdown, handleClickSendCode]);

  useEffect(() => {
    /** 用户级别校验验证码 */
    void getRobotCode();
  }, [getRobotCode]);

  const renderErrmsg = useMemo(() => {
    for (const [key, value] of Object.entries(formState.errors)) {
      const { message } = value;

      if (key === 'email') return message;
      if (key === 'captcha') return message;
      if (key === 'vcode') return message;
    }

    return null;
  }, [formState]);

  const handleClickHelp = useCallback(() => {
    window.report('mail_verification_click_aisee');

    if (isAppMarket()) {
      void Ipc.BroadCastMessage(JSON.stringify({
        type: 'aisee',
        action: 'show',
      }));
    } else {
      window.open('https://webapp.gameloop.com/syzs/syzsweb/aisee.html');
    }
  }, []);

  useEffect(() => {
    window.report('mail_verification_view');
  }, []);

  return (
    <div className="login-v2-mail-verification">
      <BaseModel
        style={{
          width: '456px',
          height: 'auto',
        }}
        showClose
        onClose={() => postMessageToParent('USER_BIND_MAIL_FAIL', null)}
      >
        <form>
          <div className="inner">
            <p className="title">{i18n.l('MailVerification')}</p>
            <p className="sub-title">{i18n.l('VerifiedMail')}</p>
            {captchaImg && (
              <Input
                name="captcha"
                register={register}
                placeholder={i18n.l('NotRobot')}
                style={{ width: '274px' }}
                registerOptions={{
                  maxLength: {
                    value: 4,
                    message: i18n.l('VcodeIError'),
                  },
                  minLength: {
                    value: 4,
                    message: i18n.l('VcodeIError'),
                  },
                  required: i18n.l('CheckRequiredField'),
                }}
                error={!!errors.captcha}
                handleBlurChange={() => handleBlurChange('captcha')}
              >
                <img alt="captcha" className="captcha-img" src={captchaImg} onClick={() => getRobotCode(email)} />
              </Input>
            )}
            <Input
              name="email"
              register={register}
              placeholder={i18n.l('EmailAddress')}
              registerOptions={{
                pattern: {
                  value: /^[0-9a-zA-Z_.-]+[@][0-9a-zA-Z_.-]+([.][a-zA-Z]+){1,2}$/,
                  message: i18n.l('EmailIError'),
                },
                required: i18n.l('CheckRequiredField'),
              }}
              error={!!errors.email}
              handleBlurChange={() => handleBlurChange('email')}
            >
              {sendCodeButton}
            </Input>
            <Input
              name="vcode"
              register={register}
              placeholder={i18n.l('EmailVCode')}
              registerOptions={{
                maxLength: {
                  value: 6,
                  message: i18n.l('EvcodeIError'),
                },
                minLength: {
                  value: 6,
                  message: i18n.l('EvcodeIError'),
                },
                required: i18n.l('CheckRequiredField'),
              }}
              error={!!errors.vcode}
              handleBlurChange={() => handleBlurChange('vcode')}
            />
            <div className="footer">
              {renderErrmsg && <p className="error-msg">{renderErrmsg}</p>}
              <span className="tips">
                {i18n.l('CanNotReceive')}
                <a className="help" onClick={handleClickHelp}>{i18n.l('Help')}</a>
              </span>
              <Button disable={hasFormError} onClick={submitMailVerification} />
            </div>
          </div>
        </form>

      </BaseModel>
    </div>
  );
};

export default MailVerificationMain;
